import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FlaskConical,
  Search,
  Calendar,
  Clock,
  FileText,
  Download,
  Eye,
  RefreshCw,
  MapPin,
  User,
  AlertCircle,
  CheckCircle,
  Plus,
  TestTube,
  DollarSign,
  Users,
  Loader2,
  ChevronRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { toast } from '@/hooks/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  getPanels,
  getClinicOrders,
  getRequisitionPDF,
  LabOrder,
  LabOrderStatus,
  LabPanel,
  getOrderStatusLabel,
  getOrderStatusColor,
  getTreatmentAreaLabel,
} from '@/services/labService';
import { resolvePanelName } from '@/lib/labUtils';
import { patientService } from '@/services/patientService';
import { useClinic } from '@/contexts/ClinicContext';
import LabOrderDetailSheet from '@/components/labs/LabOrderDetailSheet';

// Status Badge Component
const StatusBadge: React.FC<{ status: LabOrderStatus }> = ({ status }) => {
  const colorClass = getOrderStatusColor(status);
  return (
    <Badge className={`${colorClass} border-0 px-2 py-0.5`} style={{ fontSize: '0.7rem' }}>
      {getOrderStatusLabel(status)}
    </Badge>
  );
};

// Color palette for patient icons
const PATIENT_COLORS = [
  { bg: 'bg-blue-100', text: 'text-blue-600' },
  { bg: 'bg-purple-100', text: 'text-purple-600' },
  { bg: 'bg-pink-100', text: 'text-pink-600' },
  { bg: 'bg-orange-100', text: 'text-orange-600' },
  { bg: 'bg-amber-100', text: 'text-amber-600' },
  { bg: 'bg-cyan-100', text: 'text-cyan-600' },
  { bg: 'bg-indigo-100', text: 'text-indigo-600' },
  { bg: 'bg-rose-100', text: 'text-rose-600' },
  { bg: 'bg-violet-100', text: 'text-violet-600' },
  { bg: 'bg-sky-100', text: 'text-sky-600' },
];

// Order Row Component
const OrderRow: React.FC<{
  order: LabOrder;
  onViewOrder: (orderId: string) => void;
  onViewPatient: (patientId: string) => void;
  onDownloadRequisition: (orderId: string) => void;
  index: number;
}> = ({ order, onViewOrder, onViewPatient, onDownloadRequisition, index }) => {
  const formatDateTime = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const patientName = order.patient ? `${order.patient.full_name}` : 'Unknown Patient';
  const colorIndex = index % PATIENT_COLORS.length;
  const iconColor = PATIENT_COLORS[colorIndex];

  return (
    <tr className='border-b border-slate-100 transition-colors hover:bg-slate-50/50'>
      <td className='px-3 py-2.5'>
        <button
          onClick={() => order.patient_id && onViewPatient(order.patient_id)}
          className='flex items-center gap-2.5 text-left transition-colors w-full'
        >
          <div className={`w-8 h-8 ${iconColor.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
            <User className={`h-4 w-4 ${iconColor.text}`} />
          </div>
          <div className='min-w-0 flex-1'>
            <div className='text-sm font-medium text-slate-900 hover:text-brand-teal truncate capitalize'>{patientName}</div>
            {order.patient?.email && (
              <div className='text-[11px] text-slate-500 truncate'>{order.patient.email}</div>
            )}
          </div>
        </button>
      </td>
      <td className='px-3 py-2.5'>
        <div className='flex flex-col gap-0.5'>
          <div className='flex items-center gap-1.5'>
            <FlaskConical className='h-3.5 w-3.5 text-brand-teal' />
            <span className='text-sm text-slate-700'>
              {resolvePanelName(order, 'Unknown Panel')}
            </span>
          </div>
          {order.provider_name && (
            <span className='ml-5 text-[10px] font-medium text-slate-400 uppercase tracking-wider'>
              {order.provider_name}
            </span>
          )}
        </div>
      </td>
      <td className='px-3 py-2.5'>
        <StatusBadge status={order.status} />
      </td>
      <td className='px-3 py-2.5'>
        <div className='flex items-center gap-1'>
          {order.collection_method === 'walk_in' ? (
            <>
              <MapPin className='h-3 w-3 text-slate-400' />
              <span className='text-xs text-slate-600'>Walk-in</span>
            </>
          ) : (
            <>
              <Clock className='h-3 w-3 text-slate-400' />
              <span className='text-xs text-slate-600'>At Home</span>
            </>
          )}
        </div>
      </td>
      <td className='px-3 py-2.5'>
        <div className='text-xs text-slate-600'>{formatDateTime(order.created_at)}</div>
      </td>
      <td className='px-3 py-2.5'>
        <div className='flex items-center gap-1.5'>
          {order.results && order.results.length > 0 && (
            <Badge variant='outline' className='text-[10px] px-1.5 py-0 border-emerald-200 bg-emerald-50 text-emerald-700'>
              {order.results.length} {order.results.length === 1 ? 'result' : 'results'}
            </Badge>
          )}
          <Button
            variant='outline'
            size='sm'
            onClick={() => onViewOrder(order.id)}
            className='h-7 w-7 p-0'
            title='View order details'
          >
            <Eye className='h-3.5 w-3.5' />
          </Button>
          {order.requisition_url && (
            <Button
              variant='outline'
              size='sm'
              onClick={() => onDownloadRequisition(order.id)}
              className='h-7 w-7 p-0'
              title='Download requisition'
            >
              <Download className='h-3.5 w-3.5' />
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
};

// Test Card Component
const PanelCard: React.FC<{
  panel: LabPanel;
  onOrderForPatient: (panel: LabPanel) => void;
}> = ({ panel, onOrderForPatient }) => {
  const [expanded, setExpanded] = useState(false);
  const markers = panel.markers || [];
  const price = panel.patientPrice || panel.price;
  const method = panel.collectionMethod || panel.collection_method;

  const getMethodLabel = (m?: string) => {
    if (m === 'at_home_phlebotomy') return 'At Home Phlebotomy';
    if (m === 'walk_in_test' || m === 'walk_in') return 'Walk-in';
    return m || '';
  };

  return (
    <Card className='border border-slate-200 transition-shadow hover:shadow-sm'>
      <CardContent className='p-3'>
        <div className='flex items-start justify-between gap-3'>
          <div className='flex-1 min-w-0'>
            <div className='flex items-center gap-1.5'>
              <TestTube className='h-3.5 w-3.5 text-brand-teal flex-shrink-0' />
              <span className='text-sm font-medium text-slate-900'>{panel.name}</span>
            </div>
            <div className='flex items-center gap-2 mt-1'>
              {method && (
                <Badge variant='outline' className='text-[10px] px-1.5 py-0'>
                  {getMethodLabel(method)}
                </Badge>
              )}
              {(panel.fastingRequired || panel.fasting_required) && (
                <Badge variant='outline' className='text-[10px] px-1.5 py-0 text-amber-700 border-amber-200'>
                  Fasting
                </Badge>
              )}
              {panel.turnaroundDays && (
                <span className='text-[10px] text-slate-400'>~{panel.turnaroundDays}d</span>
              )}
            </div>
            {panel.description && (
              <p className='mt-1.5 text-xs text-slate-500 line-clamp-2'>{panel.description}</p>
            )}
            {markers.length > 0 && (
              <div className='mt-1.5'>
                <div className='flex flex-wrap gap-1'>
                  {markers.slice(0, expanded ? markers.length : 4).map((marker, idx) => (
                    <Badge key={idx} variant='secondary' className='text-[10px] px-1.5 py-0'>
                      {marker.name}
                    </Badge>
                  ))}
                  {!expanded && markers.length > 4 && (
                    <button
                      type='button'
                      onClick={e => {
                        e.stopPropagation();
                        setExpanded(true);
                      }}
                      className='cursor-pointer'
                    >
                      <Badge variant='outline' className='text-[10px] px-1.5 py-0'>
                        +{markers.length - 4} more
                      </Badge>
                    </button>
                  )}
                  {expanded && markers.length > 4 && (
                    <button
                      type='button'
                      onClick={e => {
                        e.stopPropagation();
                        setExpanded(false);
                      }}
                      className='cursor-pointer'
                    >
                      <Badge variant='outline' className='text-[10px] px-1.5 py-0'>
                        Show less
                      </Badge>
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className='text-right flex-shrink-0'>
            {price != null && (
              <div className='flex items-center gap-0.5 text-base font-semibold text-emerald-600'>
                <DollarSign className='h-3.5 w-3.5' />
                {Number(price).toFixed(0)}
              </div>
            )}
            <Button size='sm' className='mt-2 h-7 text-xs' onClick={() => onOrderForPatient(panel)}>
              Order
              <ChevronRight className='ml-1 h-3 w-3' />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Patient Search Dialog
const PatientSearchDialog: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectPatient: (patient: any) => void;
  selectedPanel?: LabPanel | null;
  clinicId?: string;
}> = ({ open, onOpenChange, onSelectPatient, selectedPanel, clinicId }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [patients, setPatients] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const loadInitialPatients = async () => {
    if (!clinicId) return;
    try {
      setLoading(true);
      const response = await patientService.getPatientsForDropdown('', clinicId);
      setPatients(response?.data?.patients || []);
    } catch (error) {
      console.error('Patient list error:', error);
      toast({
        title: 'Load Failed',
        description: 'Could not load patients',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (term: string) => {
    if (!term || !clinicId) return;
    try {
      setLoading(true);
      const response = await patientService.getPatientsForDropdown(term, clinicId);
      setPatients(response?.data?.patients || []);
    } catch (error) {
      console.error('Patient search error:', error);
      toast({
        title: 'Search Failed',
        description: 'Could not search patients',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    const term = searchTerm.trim();
    if (!clinicId) return;
    if (!term) {
      loadInitialPatients();
      return;
    }
    const timeoutId = window.setTimeout(() => {
      handleSearch(term);
    }, 400);
    return () => window.clearTimeout(timeoutId);
  }, [searchTerm, clinicId, open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-lg'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2 text-base'>
            <Users className='h-4 w-4' />
            Select Patient
          </DialogTitle>
          <DialogDescription className='text-xs'>
            {selectedPanel
              ? `Order "${selectedPanel.name}" for a patient`
              : 'Search for a patient to order labs'}
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-3'>
          <div className='flex gap-2'>
            <Input
              placeholder='Search by name or email...'
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch(searchTerm.trim())}
              className='flex-1 h-9 text-sm'
            />
            <Button onClick={() => handleSearch(searchTerm.trim())} disabled={loading} size='sm' className='h-9 w-9 p-0'>
              {loading ? (
                <Loader2 className='h-3.5 w-3.5 animate-spin' />
              ) : (
                <Search className='h-3.5 w-3.5' />
              )}
            </Button>
          </div>

          {loading && (
            <div className='space-y-1.5'>
              {[1, 2, 3, 4].map(i => (
                <Skeleton key={i} className='h-12 w-full' />
              ))}
            </div>
          )}

          {!loading && patients.length > 0 && (
            <div className='max-h-64 space-y-1.5 overflow-y-auto'>
              {patients.map(patient => (
                <button
                  key={patient.id}
                  onClick={() => onSelectPatient(patient)}
                  className='w-full rounded-md border border-slate-200 p-2.5 text-left transition-colors hover:bg-slate-50'
                >
                  <div className='text-sm font-medium text-slate-900'>
                    {patient.full_name ||
                      `${patient.first_name || ''} ${patient.last_name || ''}`.trim()}
                  </div>
                  <div className='text-xs text-slate-500'>{patient.email || 'No email'}</div>
                </button>
              ))}
            </div>
          )}

          {patients.length === 0 && searchTerm && !loading && (
            <div className='py-8 text-center text-slate-400'>
              <p className='text-xs'>No patients found. Try a different search term.</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Main Page Component
const LabOrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const { isPractitioner, isAdmin, clinic, isClinicStaff } = useClinic();
  const [activeTab, setActiveTab] = useState('orders');
  const [orders, setOrders] = useState<LabOrder[]>([]);
  const [availablePanels, setAvailablePanels] = useState<LabPanel[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [loadingPanels, setLoadingPanels] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Patient search dialog
  const [patientDialogOpen, setPatientDialogOpen] = useState(false);
  const [selectedPanel, setSelectedPanel] = useState<LabPanel | null>(null);

  // Order detail sheet
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [orderDetailSheetOpen, setOrderDetailSheetOpen] = useState(false);

  // Stats
  const stats = {
    total: orders.length,
    pending: orders.filter(o =>
      ['pending', 'pending_appointment', 'requisition_ready'].includes(o.status)
    ).length,
    scheduled: orders.filter(o => o.status === 'scheduled').length,
    completed: orders.filter(o => o.status === 'completed').length,
  };

  const fetchOrders = async () => {
    try {
      setLoadingOrders(true);
      const statusParam = statusFilter !== 'all' ? statusFilter : undefined;
      const { orders: data } = await getClinicOrders(clinic?.id || '', { status: statusParam });
      setOrders(data);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load orders';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setLoadingOrders(false);
    }
  };

  const fetchAvailablePanels = async () => {
    try {
      setLoadingPanels(true);
      const data = await getPanels();
      setAvailablePanels(data);
    } catch (error) {
      console.error('Error loading available panels:', error);
    } finally {
      setLoadingPanels(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    fetchAvailablePanels();
  }, [statusFilter]);

  const handleDownloadRequisition = async (orderId: string) => {
    try {
      const blob = await getRequisitionPDF(orderId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `requisition-${orderId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      toast({
        title: 'Download failed',
        description: 'Failed to download requisition PDF.',
        variant: 'destructive',
      });
    }
  };

  const handleViewOrder = (orderId: string) => {
    setSelectedOrderId(orderId);
    setOrderDetailSheetOpen(true);
  };

  const getPortalBasePath = () => {
    if (isPractitioner) return '/practitioner-portal';
    // Admin + clinic_staff both live at root.
    return '';
  };

  const handleAnalyzeIntelliHealth = (patientId: string) => {
    const basePath = getPortalBasePath();
    navigate(`${basePath}/patients/${patientId}?tab=intellihealth`);
  };

  const handleViewPatient = (patientId: string) => {
    navigate(`${getPortalBasePath()}/patients/${patientId}`);
  };

  const handleViewResults = (patientId: string) => {
    navigate(`${getPortalBasePath()}/labs/results/${patientId}`);
  };

  const handleOrderForPatient = (panel: LabPanel) => {
    setSelectedPanel(panel);
    setPatientDialogOpen(true);
  };

  const handleNewOrder = () => {
    setSelectedPanel(null);
    setPatientDialogOpen(true);
  };

  const handleSelectPatient = (patient: any) => {
    setPatientDialogOpen(false);
    const basePath = getPortalBasePath();
    console.log('basePath', basePath);
    // Navigate to patient detail with labs tab active
    navigate(`${basePath}/patients/${patient.id}?tab=labs`);
  };

  const filteredOrders = orders.filter(order => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    const patientName = order.patient
      ? `${order.patient.first_name} ${order.patient.last_name}`.toLowerCase()
      : '';
    const panelName = order.panel?.name?.toLowerCase() || '';
    return patientName.includes(term) || panelName.includes(term);
  });

  return (
    <div className='flex flex-col h-full'>
      {/* Page Header */}
      <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4'>
        <div className='flex items-center gap-3'>
          {/* <div className='rounded-lg bg-brand-teal/10 p-2'>
            <FlaskConical className='h-5 w-5 text-brand-teal' />
          </div> */}
          <div>
            <h1 className='font-montserrat text-xl font-bold tracking-tight text-slate-900 sm:text-2xl'>Lab Orders</h1>
            <p className='mt-0.5 text-sm text-slate-500'>Manage patient lab orders and results</p>
          </div>
        </div>
        <div className='flex gap-2'>
          <Button
            onClick={() => {
              fetchOrders();
              fetchAvailablePanels();
            }}
            variant='outline'
            size='sm'
            className='h-9 gap-1.5'
          >
            <RefreshCw className='h-3.5 w-3.5' />
            Refresh
          </Button>
          <Button onClick={handleNewOrder} size='sm' className='h-9 gap-1.5'>
            <Plus className='h-3.5 w-3.5' />
            New Order
          </Button>
        </div>
      </div>

      <div className='space-y-4 flex-1 overflow-auto'>
        {/* Stats Cards */}
        <div className='grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4'>
          <Card className='border border-slate-200'>
            <CardContent className='p-3'>
              <div className='flex items-center gap-2.5'>
                <div className='rounded-md bg-blue-50 p-2'>
                  <FileText className='h-4 w-4 text-blue-600' />
                </div>
                <div>
                  <p className='text-xl font-bold text-slate-900'>{stats.total}</p>
                  <p className='text-xs text-slate-500'>Total Orders</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className='border border-slate-200'>
            <CardContent className='p-3'>
              <div className='flex items-center gap-2.5'>
                <div className='rounded-md bg-amber-50 p-2'>
                  <AlertCircle className='h-4 w-4 text-amber-600' />
                </div>
                <div>
                  <p className='text-xl font-bold text-slate-900'>{stats.pending}</p>
                  <p className='text-xs text-slate-500'>Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className='border border-slate-200'>
            <CardContent className='p-3'>
              <div className='flex items-center gap-2.5'>
                <div className='rounded-md bg-indigo-50 p-2'>
                  <Calendar className='h-4 w-4 text-indigo-600' />
                </div>
                <div>
                  <p className='text-xl font-bold text-slate-900'>{stats.scheduled}</p>
                  <p className='text-xs text-slate-500'>Scheduled</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className='border border-slate-200'>
            <CardContent className='p-3'>
              <div className='flex items-center gap-2.5'>
                <div className='rounded-md bg-emerald-50 p-2'>
                  <CheckCircle className='h-4 w-4 text-emerald-600' />
                </div>
                <div>
                  <p className='text-xl font-bold text-slate-900'>{stats.completed}</p>
                  <p className='text-xs text-slate-500'>Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value='orders' className='gap-2'>
              <FileText className='h-4 w-4' />
              Orders
            </TabsTrigger>
            <TabsTrigger value='panels' className='gap-2'>
              <TestTube className='h-4 w-4' />
              Available Panels
            </TabsTrigger>
          </TabsList>

          {/* Orders Tab */}
          <TabsContent value='orders' className='mt-4'>
            {/* Filters */}
            <Card className='mb-4 border border-slate-200'>
              <CardContent className='p-3'>
                <div className='flex flex-col gap-3 sm:flex-row'>
                  <div className='relative flex-1'>
                    <Search className='absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400' />
                    <Input
                      placeholder='Search by patient or panel name...'
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                      className='pl-9 h-9 text-sm'
                    />
                  </div>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className='w-full sm:w-48 h-9 text-sm'>
                      <SelectValue placeholder='Filter by status' />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='all'>All Status</SelectItem>
                      <SelectItem value='pending'>Pending</SelectItem>
                      <SelectItem value='pending_appointment'>Needs Appointment</SelectItem>
                      <SelectItem value='requisition_ready'>Requisition Ready</SelectItem>
                      <SelectItem value='scheduled'>Scheduled</SelectItem>
                      <SelectItem value='processing'>Processing</SelectItem>
                      <SelectItem value='completed'>Completed</SelectItem>
                      <SelectItem value='cancelled'>Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Orders Table */}
            <Card className='border border-slate-200'>
              <CardHeader className='border-b border-slate-100 pb-3'>
                <CardTitle className='text-sm font-semibold text-slate-700'>Lab Orders</CardTitle>
              </CardHeader>
              <CardContent className='p-0'>
                {loadingOrders ? (
                  <div className='space-y-2 p-3'>
                    {[1, 2, 3, 4, 5].map(i => (
                      <Skeleton key={i} className='h-14 w-full' />
                    ))}
                  </div>
                ) : filteredOrders.length === 0 ? (
                  <div className='py-10 text-center'>
                    <FlaskConical className='mx-auto mb-3 h-10 w-10 text-slate-300' />
                    <h3 className='mb-1 text-sm font-medium text-slate-900'>No orders found</h3>
                    <p className='mb-4 text-xs text-slate-500'>
                      {searchTerm || statusFilter !== 'all'
                        ? 'Try adjusting your filters'
                        : 'Create your first lab order to get started'}
                    </p>
                    <Button onClick={handleNewOrder} size='sm' className='h-8'>
                      <Plus className='mr-1.5 h-3.5 w-3.5' />
                      Create Lab Order
                    </Button>
                  </div>
                ) : (
                  <div className='overflow-x-auto'>
                    <table className='w-full'>
                      <thead className='bg-slate-50'>
                        <tr className='border-b border-slate-200'>
                          <th className='px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-600'>
                            Patient
                          </th>
                          <th className='px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-600'>
                            Panel
                          </th>
                          <th className='px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-600'>
                            Status
                          </th>
                          <th className='px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-600'>
                            Method
                          </th>
                          <th className='px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-600'>
                            Created
                          </th>
                          <th className='px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-600'>
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className='bg-white'>
                        {filteredOrders.map((order, index) => (
                          <OrderRow
                            key={order.id}
                            order={order}
                            onViewOrder={handleViewOrder}
                            onViewPatient={handleViewPatient}
                            onDownloadRequisition={handleDownloadRequisition}
                            index={index}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Available Panels Tab */}
          <TabsContent value='panels' className='mt-4'>
            <Card className='border border-slate-200'>
              <CardHeader className='border-b border-slate-100 pb-3'>
                <CardTitle className='text-sm font-semibold text-slate-700'>Available Lab Panels</CardTitle>
                <CardDescription className='text-xs'>Select a panel to order for a patient</CardDescription>
              </CardHeader>
              <CardContent className='p-3'>
                {loadingPanels ? (
                  <div className='space-y-2'>
                    {[1, 2, 3].map(i => (
                      <Skeleton key={i} className='h-28 w-full' />
                    ))}
                  </div>
                ) : availablePanels.length === 0 ? (
                  <div className='py-10 text-center'>
                    <TestTube className='mx-auto mb-3 h-10 w-10 text-slate-300' />
                    <h3 className='mb-1 text-sm font-medium text-slate-900'>No panels available</h3>
                    <p className='text-xs text-slate-500'>Lab panels will appear here once configured by admin</p>
                  </div>
                ) : (
                  <div className='grid grid-cols-1 gap-3 lg:grid-cols-2'>
                    {availablePanels.map(panel => (
                      <PanelCard
                        key={panel.id}
                        panel={panel}
                        onOrderForPatient={handleOrderForPatient}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Patient Search Dialog */}
      <PatientSearchDialog
        open={patientDialogOpen}
        onOpenChange={setPatientDialogOpen}
        onSelectPatient={handleSelectPatient}
        selectedPanel={selectedPanel}
        clinicId={clinic?.id}
      />

      {/* Order Detail Sheet */}
      <LabOrderDetailSheet
        open={orderDetailSheetOpen}
        onOpenChange={setOrderDetailSheetOpen}
        orderId={selectedOrderId}
        onViewPatient={handleViewPatient}
        onViewResults={handleViewResults}
        onDownloadRequisition={handleDownloadRequisition}
        onAnalyzeIntelliHealth={handleAnalyzeIntelliHealth}
      />
    </div>
  );
};

export default LabOrdersPage;
